import { track } from '@generated/hooks/useTrack';
import { Mail, Megaphone, PenLine, Phone } from 'lucide-react';

const TO = 'gbrook8344@gmail.com';
const JOB = { sales: 'job_sales', freelance: 'job_freelance', phone: 'job_phone' } as const;

const POSITIONS = [
  {
    id: 'sales', icon: Megaphone, title: 'Ad Salesperson', kind: 'Commission',
    blurb: 'Meet local businesses in Red Hook, South Brooklyn and the Village, and help them reach their neighbors through our papers, website and newsletter.',
    asks: ['Your neighborhood and availability', 'Any sales experience', 'Businesses you already know'],
  },
  {
    id: 'freelance', icon: PenLine, title: 'Freelance Submissions', kind: 'Writers, photographers, artists',
    blurb: 'Pitch a story, send photographs, a column idea or illustrations. We are always looking for fresh local voices, and we pay for submissions that we print.',
    asks: ['What you want to contribute', 'A link to samples of your work', 'Which paper: Red Hook or Village'],
  },
  {
    id: 'phone', icon: Phone, title: 'Telephone Sales', kind: 'Part time, work from home',
    blurb: 'Call our list of past and prospective advertisers, renew contracts and book new ads, on your own schedule.',
    asks: ['Hours you can work', 'Any phone sales experience', 'Best number to reach you'],
  },
] as const;

function draft(title: string, asks: readonly string[]) {
  const subject = `Help Wanted: ${title}`;
  const body = `Hello George,\n\nI'm interested in the ${title} position at the Star-Revue.\n\n${asks.map((a) => `${a}:\n\n`).join('')}Name:\nPhone:\n\nThank you!`;
  const s = encodeURIComponent(subject), b = encodeURIComponent(body);
  return {
    gmail: `https://mail.google.com/mail/?view=cm&fs=1&to=${TO}&su=${s}&body=${b}`,
    outlook: `https://outlook.live.com/mail/0/deeplink/compose?to=${TO}&subject=${s}&body=${b}`,
    app: `mailto:${TO}?subject=${s}&body=${b}`,
  };
}

export function HelpWanted() {
  return (
    <section>
      <div className="border-y-4 border-double border-foreground py-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Help Wanted</div>
        <h2 className="mt-2 font-[family-name:var(--font-heading)] text-3xl font-semibold text-[hsl(var(--brand-orange))] sm:text-4xl">Work with the <strong><em>Star-Revue</em></strong></h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">We're a small local team, and we're growing. If one of these fits you, press Apply. Your email opens with a message to George, ready to fill in and send.</p>
      </div>
      <div className="mt-6 grid gap-0 border border-foreground md:grid-cols-3">
        {POSITIONS.map((p, i) => (
          <article key={p.id} className={`flex flex-col p-5 ${i > 0 ? 'border-t border-foreground md:border-l md:border-t-0' : ''}`}>
            <p.icon className="h-6 w-6" aria-hidden />
            <h3 className="mt-3 font-[family-name:var(--font-heading)] text-2xl font-semibold">{p.title}</h3>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{p.kind}</div>
            <p className="mt-3 text-sm">{p.blurb}</p>
            <div className="mt-4 text-xs font-semibold uppercase tracking-wider">Tell us</div>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">{p.asks.map((a) => <li key={a}>{a}</li>)}</ul>
            {(() => { const d = draft(p.title, p.asks); return (
              <div className="mt-5">
                <a onClick={() => track(JOB[p.id])} href={d.gmail} target="_blank" rel="noopener noreferrer" className="flex min-h-11 items-center justify-center gap-2 bg-[hsl(var(--brand-orange))] px-4 text-sm font-semibold text-primary-foreground hover:opacity-90">
                  <Mail className="h-4 w-4" />Apply with Gmail
                </a>
                <div className="mt-2 flex flex-wrap justify-center gap-x-3 text-xs">
                  <a onClick={() => track(JOB[p.id])} href={d.outlook} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center underline underline-offset-4">Outlook.com</a>
                  <a onClick={() => track(JOB[p.id])} href={d.app} className="inline-flex min-h-11 items-center underline underline-offset-4">Other email app</a>
                </div>
              </div>
            ); })()}
          </article>
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">Button not opening an email? Write to <a href={`mailto:${TO}`} className="underline">{TO}</a> with the position in the subject line.</p>
    </section>
  );
}
